import { NO_TEXT } from '@myre/common-i18n';
import { compareByLocaleFactory } from '@myre/common-primitives';
import {
  ActionsDropdownComponent,
  DropdownItemType,
  type DropdownItem,
} from '@myre/ui-primitive-actions-dropdown';
import {
  BoxLoaderComponent,
  ButtonHeight,
  SpinLoaderDirective,
} from '@myre/ui-primitive-components';
import { EllipsisDirective } from '@myre/ui-primitive-ellipsis';

import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';

import { FooterMinimalistComponent } from '../footer-minimalist/footer-minimalist.component';
import { type MenuBlock } from '../menu/menu.interfaces';
import { MenuService } from '../menu/menu.service';
import { RoutingService } from '../routing/routing.service';

import { MenuItemsComponent } from './menu-items/menu-items.component';

@Component({
  selector: 'myre-side-nav',
  templateUrl: './myre-side-nav.component.html',
  styleUrls: ['./myre-side-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatSidenavModule,
    MatMenuModule,
    MatIconModule,
    ActionsDropdownComponent,
    MenuItemsComponent,
    BoxLoaderComponent,
    SpinLoaderDirective,
    EllipsisDirective,
    FooterMinimalistComponent,
    NgOptimizedImage,
  ],
})
export class MyreSideNavComponent {
  protected readonly _routingService = inject(RoutingService);
  protected readonly _menuService = inject(MenuService);

  protected readonly _isArrowHovered = signal(false);

  protected _displayDropdownIcon = computed<boolean>(
    () => this._menuService.dropdownItems().length > 1,
  );

  protected readonly _dropdownItems = computed<DropdownItem[]>(() => {
    const dropdownItemsLength = this._menuService.dropdownItems().length;

    return this._menuService
      .dropdownItems()
      .filter(
        item =>
          dropdownItemsLength === 1 ||
          item.id !== this._menuService.selectedDropdownItem(),
      )
      .sort(compareByLocaleFactory(item => item.label))
      .map((primaryMenuItem): DropdownItem => {
        const { label } = primaryMenuItem;
        const onClick = (): void => {
          if (primaryMenuItem.onClick) {
            primaryMenuItem.onClick();
          }

          if (typeof primaryMenuItem.id === 'number') {
            this._routingService.navigateToBrother({
              keepId: false,
              commands: [primaryMenuItem.id],
            });
          }
        };

        if (primaryMenuItem.imgSource) {
          return {
            type: DropdownItemType.Logo,
            items: [
              {
                label,
                onClick,
                logoUrl: primaryMenuItem.imgSource,
                trailingIcon: primaryMenuItem.trailingIcon,
              },
            ],
          };
        }

        return {
          type: DropdownItemType.Text,
          items: [
            { label, onClick, trailingIcon: primaryMenuItem.trailingIcon },
          ],
        };
      });
  });

  protected readonly _selectedPrimaryMenuItem = computed(() =>
    this._menuService
      .dropdownItems()
      .find(item => item.id === this._menuService.selectedDropdownItem()),
  );

  protected readonly _selectedPrimaryMenuItemLabel = computed(
    () => this._selectedPrimaryMenuItem()?.label ?? NO_TEXT,
  );

  protected readonly _menuBlocks = this._menuService.menuBlocks;

  protected readonly _backButtonIsLoading = computed(
    () => this._routingService.backButtonStatus() === 'loading',
  );

  protected _trackBy(index: number, item: MenuBlock): string {
    return `${item.type}-${item.items.length}-${item.items[0].label}-${index}`;
  }

  protected readonly ButtonHeight = ButtonHeight;
}
